import { useCallback, useEffect, useRef, useState } from "react";
import { Textarea } from "./ui/textarea";

interface AutoResizingTextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  value: string | undefined;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  minHeight?: number;
  maxHeight?: number;
}

export function AutoResizingTextarea({
  value,
  onChange,
  minHeight = 100,
  maxHeight = 400,
  className = "",
  style,
  ...props
}: AutoResizingTextareaProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [userSetHeight, setUserSetHeight] = useState<number | null>(null);

  // Auto-resize textarea based on content
  const autoResizeTextarea = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = "auto";

    // Calculate auto height: min minHeight, max maxHeight for auto-expansion
    const autoHeight = Math.max(
      minHeight,
      Math.min(textarea.scrollHeight, maxHeight),
    );

    // If user has manually set a height, don't shrink below it
    if (userSetHeight !== null) {
      const finalHeight = Math.max(autoHeight, userSetHeight);
      textarea.style.height = `${finalHeight}px`;
    } else {
      textarea.style.height = `${autoHeight}px`;
    }
  }, [userSetHeight, minHeight, maxHeight]);

  // Resize on initial load and when value changes
  useEffect(() => {
    autoResizeTextarea();
  }, [value, autoResizeTextarea]);

  // Detect manual resize by user
  const handleTextareaMouseUp = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Check if the height has been manually changed
    const currentHeight = textarea.offsetHeight;
    const autoHeight = Math.max(
      minHeight,
      Math.min(textarea.scrollHeight, maxHeight),
    );

    // If current height differs from auto height, user has manually resized
    if (currentHeight !== autoHeight) {
      setUserSetHeight(currentHeight);
    }
  }, [minHeight, maxHeight]);

  // Handle change and trigger auto-resize
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e);
      autoResizeTextarea();
    },
    [onChange, autoResizeTextarea],
  );

  return (
    <Textarea
      ref={textareaRef}
      value={value}
      onChange={handleChange}
      onMouseUp={handleTextareaMouseUp}
      // Drive the CSS min-height floor from the same prop as the JS auto-grow so
      // callers can request compact fields (defaults to 100px, unchanged).
      style={{ minHeight, ...style }}
      className={`resize-y overflow-y-auto ${className}`}
      {...props}
    />
  );
}
